/**
 * WHAT THE CUSTOMER PICKED, on the office estimate screen (#1625).
 *
 * A per-device repair quote offers each failed device three tiers plus a
 * decline, and the customer answers once per device. This screen never showed
 * that answer: it listed every option's lines flat in one table, so estimate
 * 092678202 — one device — read as twelve lines, three re-tests, three city
 * fees, above a Total card that said $901.21 while the lines added to
 * $1,752.52.
 *
 * Nothing here changes a customer's answer: doing that after acceptance is a
 * reopen, with its own snapshot and re-pricing rules. The one edit it offers
 * (2026-10-28) is BEFORE an answer: drop a choice from this quote — the × on a
 * choice, or "Drop … on all devices" — with Undo (`option-removal.ts`).
 * Close Out owns the editable view (`PerDeviceQuote`), which is a different
 * question — what should we offer — asked before any of this exists.
 *
 * Three rules ride the payload rather than being re-decided here, because they
 * are business facts and not presentation (`describeDeviceGroup`):
 *
 *  1. **A decline is a dash, never `$0.00`.** `$0.00` reads as a free repair,
 *     and it is a refusal, not a product.
 *  2. **The only-option device is never badged "We recommend this."** The
 *     recommendation falls back to the most thorough tier we can price; where
 *     that is Clean & Flush, badging it would recommend the one tier the
 *     business explicitly does not stand behind.
 *  3. **Short tiers are a catalog gap, not damage.** A device offered two tiers
 *     instead of three is ordinary, and the screen says so rather than leaving
 *     the office to wonder what went wrong.
 *
 * Layout follows `threads/estimate-chosen-option/mockup.html`, approved by
 * Corbin on 2026-09-22 with three decisions: unbought options collapsed by
 * default, the declined device stays on screen, and the invoice-mismatch
 * banner is in.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Clock, X } from "lucide-react";

import { InfoButton } from "@/components/info-button";
import { formatDollars } from "@/lib/utils";
import { CHOICE_REMOVAL_HELP, type DropEverywhereTarget, dropEverywhereTargets } from "./option-removal";

export interface OptionGroupLine {
    id: string;
    description: string | null;
    qty: string;
    unit_price: string;
    line_total: string;
}

export interface DeviceOptionView {
    id: string;
    optionKey: string;
    label: string;
    sortOrder: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    isSelected: boolean;
    isDecline: boolean;
    lineIds: string[];
}

export interface DeviceGroupView {
    assetId: string;
    device: string;
    serial: string | null;
    location: string | null;
    selectedOptionId: string | null;
    options: DeviceOptionView[];
    shape: { realOptionCount: number; onlyOption: boolean; notGuaranteed: boolean; tiersShort: boolean };
    externalLineIds: string[];
}

export interface ComparativeOptionView {
    id: string;
    option_key: string;
    label: string;
    sort_order: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    lineIds: string[];
}

export interface EstimateOptionView {
    options: ComparativeOptionView[];
    deviceGroups: DeviceGroupView[] | null;
}

export interface InvoiceMismatch {
    invoiceId: string;
    invoiceNumber: string | null;
    status: string;
    invoiceTotal: number;
    invoiceLineCount: number;
    soldTotal: number;
    soldLineCount: number;
    estimateLineCount: number;
}

/**
 * Dropping a choice from this quote (2026-10-28). Passed only while the quote
 * can still be edited and nobody has answered it; absent, the groups stay
 * read-only exactly as before.
 */
export interface OptionRemovalControls {
    onRemove: (optionId: string, label: string) => void;
    onDropEverywhere: (target: DropEverywhereTarget) => void;
    pending: boolean;
}

/** The full three tiers. Fewer is ordinary — see `tiersShort`. */
const FULL_TIER_COUNT = 3;

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * The banner for an invoice that bills more than its estimate sold.
 *
 * Every converter used to copy EVERY option's lines onto the invoice (#1621).
 * That is fixed, but the invoices already written are not, and nothing would
 * ever tell the office which ones they are. It states both numbers and stops —
 * the office opens the invoice and decides.
 */
export function InvoiceMismatchBanner({ mismatch }: { mismatch: InvoiceMismatch }) {
    return (
        <div className="flex flex-wrap items-start gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1 space-y-1">
                <p>
                    <span className="font-semibold">The invoice doesn&apos;t match what was sold.</span> This estimate
                    sold <span className="font-semibold">{formatDollars(mismatch.soldTotal)}</span> across{" "}
                    {mismatch.soldLineCount} line{mismatch.soldLineCount === 1 ? "" : "s"}; invoice{" "}
                    {mismatch.invoiceNumber ?? "from this estimate"} bills{" "}
                    <span className="font-semibold">{formatDollars(mismatch.invoiceTotal)}</span> across{" "}
                    {mismatch.invoiceLineCount} — it looks to carry the lines from options the customer declined.
                </p>
                <p className="text-xs">
                    Invoices converted before this was fixed keep their extra lines. The invoice is {mismatch.status}.
                </p>
                <Link
                    to={`/invoices/${mismatch.invoiceId}`}
                    className="inline-block text-sm font-semibold underline underline-offset-2"
                >
                    Open the invoice →
                </Link>
            </div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */

export function EstimateOptionGroups({
    view,
    lines,
    status,
    soldTotal,
    removal,
}: {
    view: EstimateOptionView;
    lines: OptionGroupLine[];
    /** The estimate's status — decides whether a choice is an answer or a wait. */
    status: string;
    /**
     * The estimate's own subtotal — base + whichever option each group chose.
     * Passed in rather than re-summed from the groups so the strip and the
     * Total card cannot print two different numbers for one sale: summing the
     * chosen options alone drops the device work that belongs to no option
     * (`externalLineIds`), which is how the strip read $2,418.13 under a Total
     * card saying $2,506.13.
     */
    soldTotal: number;
    /** The × and "Drop … on all devices" controls; omit for read-only. */
    removal?: OptionRemovalControls;
}) {
    const groups = view.deviceGroups;
    const lineById = new Map(lines.map((l) => [l.id, l]));

    // Per-device wins when present: a per-device quote can also be flagged
    // `choose_one`, in which case the comparative list is meaningless noise.
    if (groups && groups.length > 0) {
        return (
            <PerDeviceGroups
                groups={groups}
                lineById={lineById}
                status={status}
                soldTotal={soldTotal}
                removal={removal}
            />
        );
    }
    if (view.options.length > 0) {
        return <ComparativeGroup options={view.options} lineById={lineById} status={status} />;
    }
    return null;
}

/* ── per-device (#1120) ──────────────────────────────────────────────────── */

function PerDeviceGroups({
    groups,
    lineById,
    status,
    soldTotal,
    removal,
}: {
    groups: DeviceGroupView[];
    lineById: Map<string, OptionGroupLine>;
    status: string;
    soldTotal: number;
    removal?: OptionRemovalControls;
}) {
    const answered = status === "accepted";
    // Removing a choice only makes sense before the customer has answered.
    const canRemove = !!removal && !answered;
    const dropTargets = canRemove ? dropEverywhereTargets(groups) : [];
    const isDeclined = (g: DeviceGroupView) => g.options.find((o) => o.isSelected)?.isDecline === true;
    const bought = groups.filter((g) => {
        const sel = g.options.find((o) => o.isSelected);
        return sel && !sel.isDecline;
    });
    const declined = groups.filter(isDeclined);
    // Bought first, declined last — the mockup's rule. A refusal at the top of
    // the screen reads as the headline of the sale.
    const ordered = [...groups].sort((a, b) => Number(isDeclined(a)) - Number(isDeclined(b)));
    const declinedTotal = declined.reduce(
        (n, g) => n + Math.max(0, ...g.options.filter((o) => !o.isDecline).map((o) => o.total)),
        0,
    );

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-shell-foreground">
                    {answered ? "What the customer bought" : "What the customer was offered"}
                </h2>
            </div>

            {canRemove && removal && (
                <div
                    className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm"
                    data-testid="choice-removal-bar"
                >
                    <span className="text-muted-foreground">Press × on a choice to take it off this quote.</span>
                    {dropTargets.map((t) => (
                        <button
                            key={t.optionKey}
                            type="button"
                            disabled={removal.pending}
                            onClick={() => removal.onDropEverywhere(t)}
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold hover:bg-accent/40 disabled:cursor-not-allowed"
                        >
                            <X className="h-3.5 w-3.5" />
                            Drop {t.name} on all devices
                        </button>
                    ))}
                    <InfoButton topic={CHOICE_REMOVAL_HELP} />
                </div>
            )}

            {!answered && (
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="flex-1">
                        <span className="font-semibold">Waiting on the customer.</span> Nothing has been chosen yet, so
                        no price here is a sale — the totals below are what each option would cost.
                    </span>
                </div>
            )}

            {/* Several devices answered separately is the case the flat table
                handled worst: "what did they actually buy" took reading twenty
                lines. One strip, first, answers it. */}
            {answered && groups.length > 1 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-muted/40 px-4 py-2 text-sm">
                    <span>
                        <span className="font-semibold">
                            {bought.length} of {groups.length} device{groups.length === 1 ? "" : "s"}
                        </span>{" "}
                        bought
                    </span>
                    {declined.length > 0 && (
                        <span className="text-foreground/80">
                            <span className="font-semibold">{declined.length} declined</span>
                        </span>
                    )}
                    <span>
                        Sold <span className="font-semibold">{formatDollars(soldTotal)}</span>
                    </span>
                    {declinedTotal > 0 && (
                        <span
                            className="text-foreground/70"
                            title="The recommended tier on each declined device — what the work would have cost."
                        >
                            Turned down {formatDollars(declinedTotal)}
                        </span>
                    )}
                </div>
            )}

            {ordered.map((g) => (
                <DeviceGroupCard
                    key={g.assetId}
                    group={g}
                    lineById={lineById}
                    answered={answered}
                    removal={canRemove ? removal : undefined}
                />
            ))}

            {declined.length > 0 && answered && (
                <p className="text-xs text-foreground/70">
                    A declined device is not scheduled and not billed — it stays off the repair job and off the invoice.
                    It still failed its test, so it keeps its compliance clock.
                </p>
            )}
        </div>
    );
}

function DeviceGroupCard({
    group,
    lineById,
    answered,
    removal,
}: {
    group: DeviceGroupView;
    lineById: Map<string, OptionGroupLine>;
    answered: boolean;
    removal?: OptionRemovalControls;
}) {
    const selected = group.options.find((o) => o.isSelected) ?? null;
    const isDeclined = selected?.isDecline === true;
    const externals = group.externalLineIds.map((id) => lineById.get(id)).filter(Boolean) as OptionGroupLine[];

    return (
        <div className={`overflow-hidden rounded-lg border bg-card ${isDeclined ? "opacity-70" : ""}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted/50 px-3 py-2">
                <span className="text-sm font-semibold">{group.device}</span>
                <span className="text-xs text-foreground/70">
                    {[group.serial ? `S/N ${group.serial}` : null, group.location].filter(Boolean).join(" · ")}
                </span>
                <span className="flex-1" />
                {answered && selected ? (
                    isDeclined ? (
                        <span className="rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-700 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300">
                            Declined
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200">
                            <Check className="h-3 w-3" />
                            {selected.label}
                        </span>
                    )
                ) : (
                    <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-200">
                        No answer yet
                    </span>
                )}
            </div>

            {group.options.map((o) => (
                <OptionRowView
                    key={o.id}
                    option={o}
                    lineById={lineById}
                    answered={answered}
                    // The recommendation is the most thorough tier we can price.
                    // Two cases never carry the badge: a device whose only
                    // option is Clean & Flush (the one tier the business does
                    // not stand behind), and a device the customer declined —
                    // recommending work they already refused is noise.
                    recommended={!group.shape.notGuaranteed && !isDeclined && isRecommended(group, o)}
                    removal={removal}
                    deviceName={group.device}
                />
            ))}

            {group.shape.tiersShort && (
                <p className="border-t bg-muted/30 px-3 py-2 text-xs text-foreground/75">
                    <span className="font-semibold">
                        {group.shape.realOptionCount} of {FULL_TIER_COUNT} tiers
                    </span>{" "}
                    — the catalog can&apos;t price the rest for this device, so they were never offered. Not an error.
                    {group.shape.notGuaranteed && (
                        <>
                            {" "}
                            A Clean &amp; Flush only helps if the problem is debris —{" "}
                            <span className="font-semibold">not guaranteed</span>.
                        </>
                    )}
                </p>
            )}

            {externals.length > 0 && (
                <div className="border-t bg-muted/20 px-3 py-2 text-xs">
                    <p className="mb-1 font-semibold">Added to whichever option they pick</p>
                    {externals.map((l) => (
                        <div key={l.id} className="flex justify-between gap-3 py-0.5">
                            <span className="text-foreground/80">{l.description}</span>
                            <span className="tabular-nums">{formatDollars(Number(l.line_total))}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** The most thorough priced tier — what the seeder pre-selects. */
function isRecommended(group: DeviceGroupView, option: DeviceOptionView): boolean {
    if (option.isDecline) return false;
    const real = group.options.filter((o) => !o.isDecline);
    const best = real.reduce<DeviceOptionView | null>((a, b) => (a && a.total >= b.total ? a : b), null);
    return best?.id === option.id;
}

/* ── comparative (#380) ──────────────────────────────────────────────────── */

function ComparativeGroup({
    options,
    lineById,
    status,
}: {
    options: ComparativeOptionView[];
    lineById: Map<string, OptionGroupLine>;
    status: string;
}) {
    const answered = status === "accepted";
    // The #380 shape records its choice on the estimate, and the server marks
    // the chosen row by omitting nothing — so the selected one is whichever the
    // option list reports as carrying the estimate's stored selection. Where
    // that is absent (an unanswered quote) every option reads at equal weight.
    return (
        <div className="space-y-3">
            <h2 className="text-lg font-semibold text-shell-foreground">
                {answered ? "What the customer bought" : "What the customer was offered"}
            </h2>
            <div className="overflow-hidden rounded-lg border bg-card">
                <div className="flex flex-wrap items-center gap-x-3 border-b bg-muted/50 px-3 py-2">
                    <span className="text-sm font-semibold">This quote</span>
                    <span className="text-xs text-foreground/70">Repair or replace — the customer picks one</span>
                </div>
                {options.map((o) => (
                    <OptionRowView
                        key={o.id}
                        option={{
                            id: o.id,
                            optionKey: o.option_key,
                            label: o.label,
                            sortOrder: o.sort_order,
                            subtotal: o.subtotal,
                            taxAmount: o.taxAmount,
                            total: o.total,
                            isSelected: false,
                            isDecline: false,
                            lineIds: o.lineIds,
                        }}
                        lineById={lineById}
                        answered={answered}
                        recommended={false}
                    />
                ))}
            </div>
        </div>
    );
}

/* ── one option row ──────────────────────────────────────────────────────── */

/**
 * Collapsed to one all-in price by default (Corbin, 2026-09-22 on the mockup).
 * The chosen option opens on load, because that is the work; everything else
 * opens on click. Nothing is hidden — twelve lines for one device is what
 * started this.
 */
function OptionRowView({
    option,
    lineById,
    answered,
    recommended,
    removal,
    deviceName,
}: {
    option: DeviceOptionView;
    lineById: Map<string, OptionGroupLine>;
    answered: boolean;
    recommended: boolean;
    removal?: OptionRemovalControls;
    deviceName?: string;
}) {
    const [open, setOpen] = useState(option.isSelected && !option.isDecline);
    const optionLines = option.lineIds.map((id) => lineById.get(id)).filter(Boolean) as OptionGroupLine[];
    const dimmed = answered && !option.isSelected;
    const expandable = optionLines.length > 0;

    // The decline is not a choice anyone removes: it goes by itself with the
    // device's last real choice.
    const removable = !!removal && !option.isDecline;

    return (
        <>
            <div className="flex items-stretch border-b last:border-b-0">
                <button
                    type="button"
                    onClick={() => expandable && setOpen((v) => !v)}
                    aria-expanded={expandable ? open : undefined}
                    disabled={!expandable}
                    className={[
                        "flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm",
                        option.isSelected && !option.isDecline ? "bg-emerald-50/70 dark:bg-emerald-950/30" : "",
                        dimmed ? "text-foreground/60" : "",
                        expandable ? "hover:bg-accent/40" : "cursor-default",
                    ].join(" ")}
                >
                    <span className="w-4 shrink-0 text-foreground/50">
                        {expandable ? (
                            open ? (
                                <ChevronDown className="h-4 w-4" />
                            ) : (
                                <ChevronRight className="h-4 w-4" />
                            )
                        ) : null}
                    </span>
                    <span className={option.isSelected && !option.isDecline ? "flex-1 font-semibold" : "flex-1"}>
                        {option.label}
                    </span>
                    {recommended && (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-950/60 dark:text-violet-200">
                            We recommend this
                        </span>
                    )}
                    {/* A decline is a dash. $0.00 reads as a free repair. */}
                    <span className="shrink-0 font-semibold tabular-nums">
                        {option.isDecline ? "—" : formatDollars(option.total)}
                    </span>
                </button>
                {removable && removal && (
                    <button
                        type="button"
                        disabled={removal.pending}
                        onClick={() => removal.onRemove(option.id, option.label)}
                        aria-label={`Remove ${option.label}${deviceName ? ` from ${deviceName}` : ""} on this quote`}
                        title="Remove this choice from this quote only"
                        className="flex w-10 shrink-0 items-center justify-center text-muted-foreground hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {open && optionLines.length > 0 && (
                <div className="border-b bg-muted/20 px-3 py-1.5 last:border-b-0">
                    <table className="w-full text-xs">
                        <tbody>
                            {optionLines.map((l) => (
                                <tr key={l.id} className="border-b border-border/40 last:border-b-0">
                                    <td className="py-1 pl-4 pr-2 text-foreground/85">{l.description}</td>
                                    <td className="w-12 py-1 text-right tabular-nums text-foreground/70">
                                        {Number(l.qty)}
                                    </td>
                                    <td className="w-20 py-1 text-right tabular-nums text-foreground/70">
                                        {formatDollars(Number(l.unit_price))}
                                    </td>
                                    <td className="w-20 py-1 text-right font-medium tabular-nums">
                                        {formatDollars(Number(l.line_total))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
